import React, { useEffect, useState, useRef } from 'react';
import './EditorPage.css';
import { useSearchParams } from 'react-router-dom';
import { fetchWebsite, getWebsiteLookupDataFromUrl, saveWebsiteWithHtml } from '../utils/api/website.utils';
import StudioEditor from '@grapesjs/studio-sdk/react';
import '@grapesjs/studio-sdk/style';
import '../styles/editor-blocks-panel.css';
import { accordionComponent } from '@grapesjs/studio-sdk-plugins';
import { openFormPickerModal } from '../components/FormsPickerModal';
import { fetchAssets, getFileType, getFileName, deleteAsset, uploadAsset } from '../utils/api/assets.utils';
import useEditorActionsIframeListener from '../hooks/useEditorActionsIframeListener';
import useUndoRedoAvailability from '../hooks/useUndoRedoAvailability';
import useStyleSpecificityFix from '../hooks/useStyleSpecificityFix';
import { createCustomVideoComponent, addCustomVideoBlocks } from '../utils/customVideoComponent';
import { UserWebsite } from '../types/UserWebsite';
import { Theme } from '../types/Theme';
import useAuthTokenIframeListener from '../hooks/useAuthTokenIframeListener';
import { getFontsDefault, getProjectFontsConfig, setupFontFaceInterceptors } from '../utils/font.utils';
import { lockAndAddTraitsForms } from '../utils/formTraits';
import { BLOCKS_TO_REMOVE, hideButtons } from '../utils/editor.utils';
import { FORM_TEMPLATES, TEMPLATES } from '../utils/forms.utils';
import { defaultBlocks } from '../utils/editorBlocks';
import { editorColors } from '../utils/editorColors';
import { setupFontWeightCustomization } from '../utils/editor/fontWeightCustomization';
import grapesjsPluginTinymce6 from '../plugins/grapesjs-plugin-tinymce6/src';
import { TINYMCE6_PLUGIN_OPTS } from '../plugins/grapesjs-plugin-tinymce6/pluginOpts';
import { applyLabelsOverride } from '../utils/labelsOverride';

const EditorPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { websiteId, websiteState, websiteType } = getWebsiteLookupDataFromUrl(searchParams);

  const [website, setWebsite] = useState<UserWebsite | Theme | null>(null);
  const [pages, setPages] = useState<any[] | null>(null);
  const [editor, setEditor] = useState<any>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const editorRef = useRef<any>(null);
  const websiteRef = useRef<UserWebsite | Theme | null>(null);
  const initialSaveDoneRef = useRef(false);

  useEditorActionsIframeListener(editor);
  useAuthTokenIframeListener();
  useUndoRedoAvailability(editor);
  useStyleSpecificityFix(editor);

  useEffect(() => {
    let alive = true;
    const loadText = (url: string) =>
      fetch(url, { cache: 'no-store' }).then(r => {
        if (!r.ok) throw new Error(`Failed to load ${url}`);
        return r.text();
      });

    (async () => {
      const loaded = await Promise.all(
        TEMPLATES.map(async t => ({
          id: t.id,
          name: t.name,
          component: await loadText(t.html),
        }))
      );
      if (alive) setPages(loaded);
    })().catch(err => {
      console.error(err);
      if (alive) setPages([]);
    });

    return () => {
      alive = false;
      setEditor(null);
    };
  }, []);

  useEffect(() => {
    const fetchWebsiteData = async () => {
      if (website === null && websiteId && websiteType) {
        setWebsite(await fetchWebsite(websiteId, websiteType));
      }
    };
    fetchWebsiteData();
    // eslint-disable-next-line
  }, [websiteId, websiteType]);

  if (
    !websiteId ||
    !websiteState ||
    !websiteType ||
    !['theme', 'user-website'].includes(websiteType) ||
    !['draft', 'published'].includes(websiteState)
  ) {
    return <div>No website id, state or type</div>;
  }

  if (editorError) return <div>Error: {editorError}</div>;

  if (website === null) {
    return <></>;
  }

  return (
    <div className="editor-page">
      <StudioEditor
        onReady={(editor: any) => {
          lockAndAddTraitsForms(editor);
          setupFontWeightCustomization(editor);
        }}
        onEditor={(editor: any) => {
          grapesjsPluginTinymce6(editor, TINYMCE6_PLUGIN_OPTS as any);

          editor.Components.addType('form-picker-placeholder', {
            isComponent: (el: any) => el?.dataset?.gjsType === 'form-picker-placeholder',
            model: {
              defaults: {
                attributes: { 'data-gjs-type': 'form-picker-placeholder' },
                droppable: false,
                editable: false,
                highlightable: true,
                components:
                  '<div style="padding:16px;border:2px dashed #bbb;border-radius:8px;text-align:center;">Choose a form…</div>',
              },
            },
            view: {
              onActive() {
                const em: any = (this as any).em;
                const ed = em?.getEditor ? em.getEditor() : em?.Editor;
                if (!ed) return;

                const model = (this as any).model;
                const phId = `form_ph_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
                model.addAttributes({ id: phId });

                openFormPickerModal(ed, { placeholderId: phId }, FORM_TEMPLATES, websiteId);
              },
            },
          });
        }}
        options={{
          pages: false,
          layout: {
            default: {
              type: 'row',
              style: { height: '100%', width: '100%' },
              children: [
                {
                  type: 'sidebarLeft',
                  attrs: { class: 'left-rail' },
                  style: { padding: 5, gap: 5, borderRightWidth: 1, zIndex: 20, alignItems: 'center', width: 42 },
                  children: [
                    {
                      type: 'button',
                      tooltip: 'Blocks',
                      icon: 'viewGridPlus',
                      editorEvents: {
                        // @ts-ignore
                        'studio:layoutToggle:layoutId2': ({ fromEvent, setState }) =>
                          setState({ active: fromEvent.isOpen }),
                      },
                      // @ts-ignore
                      onClick: ({ editor }) => {
                        editor.runCommand('studio:layoutRemove', { id: 'layoutId1' });
                        editor.runCommand('studio:layoutToggle', {
                          id: 'layoutId2',
                          layout: { type: 'panelBlocks', symbols: false },
                          header: { label: 'Blocks' },
                          placer: { type: 'static', layoutId: 'hiddenLeftContainer' },
                          style: { width: 296, overflow: 'hidden' },
                        });
                      },
                    },
                    {
                      type: 'button',
                      tooltip: 'Layers',
                      icon: 'layers',
                      editorEvents: {
                        // @ts-ignore
                        'studio:layoutToggle:layoutId1': ({ fromEvent, setState }) =>
                          setState({ active: fromEvent.isOpen }),
                      },
                      // @ts-ignore
                      onClick: ({ editor }) => {
                        editor.runCommand('studio:layoutRemove', { id: 'layoutId2' });
                        editor.runCommand('studio:layoutToggle', {
                          id: 'layoutId1',
                          layout: { type: 'panelPagesLayers' },
                          header: { label: 'Layers' },
                          placer: { type: 'static', layoutId: 'hiddenLeftContainer' },
                          style: { width: 296, overflow: 'hidden' },
                        });
                      },
                    },
                  ],
                },
                { id: 'hiddenLeftContainer', type: 'column' },
                // MIDDLE: CANVAS + TOP NAVBAR
                {
                  type: 'canvasSidebarTop',
                  // your custom topbar buttons
                  sidebarTop: {
                    rightContainer: {
                      // @ts-ignore
                      buttons: hideButtons(websiteType),
                    },
                    leftContainer: {
                      // @ts-ignore
                      buttons: ({ items }) => (items ?? []).filter((it: string) => it === 'blocks-'),
                    },
                    devices: {
                      style: { display: websiteType === 'user-website' ? 'none' : 'unset' },
                    },
                  },
                },
                { type: 'sidebarRight', style: { width: 330 } },
              ],
            },
          },
          blocks: {
            default: defaultBlocks,
          },
          fonts: {
            enableFontManager: true,
            default: ({ baseDefault }: { baseDefault: any[] }) => getFontsDefault(baseDefault),
          },
          project: {
            type: 'web',
            id: 'my-project-v4',
            default: {
              pages,
              custom: {
                globalPageSettings: {
                  fonts: getProjectFontsConfig(),
                },
              },
            },
          },
          theme: 'light',
          customTheme: {
            default: {
              colors: editorColors,
            },
          },
          plugins: [
            accordionComponent.init({
              block: { category: 'Advanced' },
              blockGroup: { category: 'Advanced' },
            }),
            ...(typeof window !== 'undefined' && (window as any)['grapesjs-super-tooltip']
              ? ['grapesjs-super-tooltip', 'grapesjs-plugin-placeholder2']
              : []),
            (editorInstance: any) => {
              applyLabelsOverride(editorInstance);
              setEditor(editorInstance);
              editorRef.current = editorInstance;

              // Log to see available properties
              // console.log("instance", editorInstance.StyleManager);
              const { Blocks } = editorInstance;
              const allBlocksId = Blocks.getAll().map((block: any) => block.id);

              const ensureWebsiteId = (cmp: any) => {
                cmp.addAttributes({ 'data-website-id': websiteId });
              };

              const isFormCmp = (cmp: any) => {
                const t = (cmp.get?.('tagName') || '').toLowerCase?.();
                return t === 'form' || cmp.is?.('form');
              };

              const findFormsInComponent = (cmp: any) => {
                if (!cmp || typeof cmp.find !== 'function') {
                  return;
                }
                try {
                  const forms = cmp.find('form');
                  if (forms && typeof forms.forEach === 'function') {
                    forms.forEach(ensureWebsiteId);
                  }
                } catch (error) {
                  // Silently ignore errors when finding forms in components
                }
              };

              editorInstance.on('load', () => {
                editorInstance.getWrapper().find('form').forEach(ensureWebsiteId);
              });
              // Force plain-text paste: GrapesJS uses canvas:dragdata (not clipboard:paste).
              // When content is dropped/pasted on canvas, override HTML with plain text.
              editorInstance.on(
                'canvas:dragdata',
                (dataTransfer: DataTransfer, result: { content: unknown; setContent: (c: unknown) => void }) => {
                  const types = dataTransfer?.types ?? [];
                  const hasHtml = types.indexOf('text/html') >= 0;
                  if (hasHtml) {
                    const plain = (dataTransfer.getData('text/plain') || '').trim();
                    if (plain) {
                      // Wrap in a simple div so the editor accepts it; line breaks as <br/>
                      const html = `<div>${plain.replace(/(?:\r\n|\r|\n)/g, '<br/>')}</div>`;
                      result.setContent(html);
                    }
                  }
                }
              );

              // Paste inside text components (RTE): always use plain text, no HTML
              const rteConfig = editorInstance.RichTextEditor?.getConfig?.() ?? editorInstance.RichTextEditor?.config;
              if (rteConfig) {
                rteConfig.onPaste = ({ ev, rte }: { ev: ClipboardEvent; rte: { doc: Document } }) => {
                  ev.preventDefault();
                  const text = (ev.clipboardData?.getData('text/plain') ?? '').replace(/(?:\r\n|\r|\n)/g, '<br/>');
                  rte.doc.execCommand('insertHTML', false, text);
                };
              }

              editorInstance.on('load', () => {
                const sm = editorInstance.StyleManager;
                sm.getSectors().forEach((sector: any) => sector.set('open', false));
              });

              editorInstance.on('component:add', (cmp: any) => {
                if (isFormCmp(cmp)) {
                  ensureWebsiteId(cmp);
                }
                // In case a whole chunk of HTML containing a form was added
                findFormsInComponent(cmp);
              });

              editorInstance.on('component:update:components', (cmp: any) => {
                if (isFormCmp(cmp)) {
                  ensureWebsiteId(cmp);
                }
                cmp.find?.('form')?.forEach?.(ensureWebsiteId);
                findFormsInComponent(cmp);
              });

              allBlocksId.forEach((blockId: any) => {
                if (BLOCKS_TO_REMOVE.includes(blockId)) {
                  Blocks.remove(blockId);
                }
              });

              editorInstance.on('load', async () => {
                // Initialize custom video component
                createCustomVideoComponent(editorInstance, websiteType);
                addCustomVideoBlocks(editorInstance);

                // Set up interceptors to prevent duplicate @font-face rules
                // setupFontFaceInterceptors(editorInstance);
                setupFontWeightCustomization(editorInstance);

                // One-time save when editor opens so publish has full data even if user never clicks Save
                if (!initialSaveDoneRef.current) {
                  initialSaveDoneRef.current = true;
                  const project = editorInstance.getProjectData?.();
                  if (project) {
                    console.log('initial load SAVING');
                    await saveWebsiteWithHtml(
                      websiteId,
                      websiteType,
                      project,
                      editorInstance,
                      websiteType === 'user-website' ? (websiteRef.current as UserWebsite) : null
                    );
                  }
                }
              });
            },
          ],
          storage: {
            type: 'self',
            onSave: async ({ project }: { project: any }) => {
              const currentEditor = editor || editorRef.current;
              if (!currentEditor) {
                return;
              }
              console.log('startsaving website');
              await saveWebsiteWithHtml(
                websiteId,
                websiteType,
                project,
                currentEditor,
                websiteType === 'user-website' ? (website as UserWebsite) : null
              );
            },

            onLoad: async () => {
              let website: UserWebsite | null = null;
              const websiteNotFoundError = 'An error occurred while fetching the website or the website is not found';
              try {
                website = (await fetchWebsite(websiteId, websiteType)) as UserWebsite;
                setWebsite(website);
                websiteRef.current = website;
              } catch (error) {
                setEditorError(websiteNotFoundError);
              }
              if (website === null) {
                setEditorError(websiteNotFoundError);
                return;
              }
              return {
                project: website.draft ?? { pages: [{ name: 'Home', component: '' }] },
              };
            },
          },
          assets: {
            storageType: 'self',
            onUpload: async ({ files, editor }: { files: any[]; editor: any }) => {
              const asset = await uploadAsset(files[0], websiteType);
              return [
                {
                  id: asset.id,
                  src: asset.url,
                  name: getFileName(asset.url),
                  mimeType: getFileType(asset.url),
                  size: 100,
                },
              ];
            },
            onDelete: async ({ assets, editor }: { assets: any[]; editor: any }) => {
              await Promise.all(assets.map(asset => deleteAsset(asset.attributes.id, websiteType)));
            },
            onLoad: async () => {
              const assets = await fetchAssets(websiteType);
              return assets.map(asset => ({
                id: asset.id,
                name: getFileName(asset.url),
                type: getFileType(asset.url),
                src: asset.url,
              }));
            },
          },
          pluginsOpts: {
            'grapesjs-super-tooltip': {
              add_new_component: true,
              more_options: true,
            },
            'grapesjs-plugin-placeholder2': {
              dragDefault: [],
              dragClasses: ['gjs-selected', 'gjs-freezed', 'animation'],
              animation: 'none',
            },
          },
          licenseKey:
            process.env.REACT_APP_GRAPEJS_LICENSE_KEY ||
            'd64d2adbc1ab451db41a296fa9351cc951a8f2e78c4f417d90a06a1de1d8ba74',
        }}
      />
    </div>
  );
};

export default EditorPage;
